import { Box, Typography } from "@mui/joy";
import Card from '@mui/joy/Card';
import Accordion from '@mui/joy/Accordion';
import AccordionSummary from '@mui/joy/AccordionSummary';
import AccordionDetails from '@mui/joy/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const wrappedTextSx = {
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  minWidth: 0,
};

const getDisplayValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'No Data Available';
  }
  if (typeof value === 'boolean') {
    return value ? 'True' : 'False';
  }
  return String(value);
};

const KeyValueRow = ({ label, value, depth = 0, onValueClick }) => (
  <Box
    sx={{
      pl: depth * 2,
      mb: 0.75,
      minWidth: 0,
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'minmax(140px, max-content) minmax(0, 1fr)',
      },
      columnGap: { xs: 0, sm: 1.5 },
      rowGap: { xs: 0.25, sm: 0 },
      alignItems: 'start',
    }}
  >
    <Typography
      level="body-sm"
      onClick={onValueClick}
      sx={{
        order: { xs: 2, sm: 2 },
        cursor: onValueClick ? 'pointer' : 'default',
        ...wrappedTextSx,
      }}
    >
      {getDisplayValue(value)}
    </Typography>
    <Typography
      level="body-sm"
      sx={{
        order: { xs: 1, sm: 1 },
        justifySelf: 'start',
        textAlign: 'left',
        ...wrappedTextSx,
      }}
    >
      <strong>{label}</strong>:
    </Typography>
  </Box>
);

const KeyValueMediaRow = ({ label, depth = 0, children }) => (
  <Box
    sx={{
      pl: depth * 2,
      mb: 0.75,
      minWidth: 0,
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'minmax(140px, max-content) minmax(0, 1fr)',
      },
      columnGap: { xs: 0, sm: 1.5 },
      rowGap: { xs: 0.25, sm: 0 },
      alignItems: 'start',
    }}
  >
    <Typography
      level="body-sm"
      sx={{
        order: { xs: 1, sm: 1 },
        justifySelf: 'start',
        textAlign: 'left',
        ...wrappedTextSx,
      }}
    >
      <strong>{label}</strong>:
    </Typography>
    <Box
      sx={{
        order: { xs: 2, sm: 2 },
        minWidth: 0,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        alignItems: 'flex-start',
      }}
    >
      {children}
    </Box>
  </Box>
);

const RecursiveItems = ({ item, handleClick, depth = 0 }) => {
  if (item === null || item === undefined) return <></>;

  return (
    <>
      {Object.entries(item).map(([key, value]) => {
        if (Array.isArray(value) && value.length === 0) {
          return null;
        }

        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          return (
            <Accordion key={key} defaultExpanded sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography level="body-sm"><strong>{key}</strong></Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Card variant="outlined" sx={{ p: 1, minWidth: 0 }}>
                  <RecursiveItems item={value} handleClick={handleClick} depth={depth + 1} />
                </Card>
              </AccordionDetails>
            </Accordion>
          );
        }

        if (Array.isArray(value)) {
          const isImageArray = key === 'image' && value.every(
            (val) =>
              val === null ||
              typeof val === 'string' ||
              (typeof val === 'object' && (val?.img || val?.image))
          );

          return (
            <Accordion key={key} defaultExpanded sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography level="body-sm"><strong>{key}:</strong></Typography>
              </AccordionSummary>
              <AccordionDetails>
                {isImageArray ? (
                  <KeyValueMediaRow label={key} depth={depth + 1}>
                    {value.map((imageData, index) => {
                      if (imageData === null) {
                        return null;
                      }

                      let src;
                      let alt;
                      if (typeof imageData === 'object' && (imageData?.img || imageData?.image)) {
                        const mimeType = imageData.type?.toLowerCase() === 'png'
                          ? 'image/png'
                          : imageData.type?.toLowerCase() === 'jpeg' || imageData.type?.toLowerCase() === 'jpg'
                            ? 'image/jpeg'
                            : 'image/png';
                        src = `data:${mimeType};base64,${imageData.img || imageData.image}`;
                        alt = `Thumbnail ${index + 1} (${imageData.type || 'PNG'})`;
                      } else if (typeof imageData === 'string') {
                        src = `data:image/png;base64,${imageData}`;
                        alt = `Thumbnail ${index + 1}`;
                      } else {
                        return (
                          <Typography key={index} level="body-sm" color="error">
                            Invalid image data at index {index}
                          </Typography>
                        );
                      }

                      return (
                        <img
                          key={index}
                          src={src}
                          alt={alt}
                          style={{
                            maxWidth: '320px',
                            maxHeight: '320px',
                            objectFit: 'contain',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                          }}
                        />
                      );
                    })}
                  </KeyValueMediaRow>
                ) : value.every((val) => val === null || typeof val !== 'object') ? (
                    <KeyValueRow
                      label={key}
                      value={value.filter((val) => val !== null && val !== undefined).join(', ')}
                      depth={depth + 1}
                    />
                ) : (
                  value.map((val, index) => (
                    val !== null && (
                      <Accordion key={index} defaultExpanded sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography level="body-sm">{index}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {typeof val === 'object' ? (
                            <Card variant="outlined" sx={{ my: 1, px: 1, minWidth: 0 }}>
                              <RecursiveItems item={val} handleClick={handleClick} depth={depth + 1} />
                            </Card>
                          ) : (
                            <KeyValueRow label={index} value={val} depth={depth + 1} />
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )
                  ))
                )}
              </AccordionDetails>
            </Accordion>
          );
        }

        if (key === 'image') {
          return (
            <KeyValueMediaRow key={key} label={key} depth={depth}>
              {typeof value === 'string' ? (
                <img
                  src={`data:image/png;base64,${value}`}
                  alt={`Image: ${key}`}
                  style={{
                    maxWidth: '320px',
                    maxHeight: '320px',
                    objectFit: 'contain',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              ) : (
                <Typography level="body-sm" color="error">
                  Invalid image data
                </Typography>
              )}
            </KeyValueMediaRow>
          );
        }

        return (
          <KeyValueRow
            key={key}
            label={key}
            value={value}
            depth={depth}
            onValueClick={key === 'page' && typeof handleClick === 'function' ? () => handleClick(() => value) : undefined}
          />
        );
      })}
    </>
  );
};

export default RecursiveItems;
